// Web-Push im Browser: Erlaubnis holen, Abo beim Push-Dienst aushandeln und
// beim eigenen Server hinterlegen. Alle Funktionen sind defensiv - auf
// Geraeten/Browsern ohne Push (u. a. iOS ausserhalb der installierten PWA)
// melden sie schlicht "nicht moeglich", statt zu werfen. Gleicher Aufbau
// wie in Brick Passport (client/src/lib/push.js).

import { api } from "./api.js";

/** Kann dieses Geraet ueberhaupt Push? (Service Worker + Push-API + Notification-API) */
export function pushMoeglichImBrowser(): boolean {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export function pushErlaubnis(): NotificationPermission | "unsupported" {
  return pushMoeglichImBrowser() ? Notification.permission : "unsupported";
}

/** Der Browser will den VAPID-Schluessel als Uint8Array, geliefert wird er als URL-sicheres Base64. */
function base64ZuBytes(base64: string): Uint8Array {
  const auffuellen = "=".repeat((4 - (base64.length % 4)) % 4);
  const roh = atob((base64 + auffuellen).replace(/-/g, "+").replace(/_/g, "/"));
  return Uint8Array.from([...roh].map((z) => z.charCodeAt(0)));
}

/**
 * Wartet auf einen betriebsbereiten Service Worker - aber NICHT ewig.
 * navigator.serviceWorker.ready loest sich nie auf, wenn gar kein Worker
 * registriert ist. Ohne diese Schranke haenge die Oberflaeche stumm fest.
 */
async function wartendeRegistrierung(msMax = 3000): Promise<ServiceWorkerRegistration | null> {
  return Promise.race([
    navigator.serviceWorker.ready,
    new Promise<null>((fertig) => setTimeout(() => fertig(null), msMax)),
  ]);
}

export type PushFehler = "nicht_moeglich" | "server_aus" | "abgelehnt" | "nicht_erteilt" | "kein_sw";

/**
 * Abo einrichten: Erlaubnis erfragen, beim Push-Dienst anmelden, beim
 * Server hinterlegen. Gibt { ok } oder { fehler } zurueck.
 */
export async function pushAnschalten(): Promise<{ ok: true } | { fehler: PushFehler }> {
  if (!pushMoeglichImBrowser()) return { fehler: "nicht_moeglich" };

  const { moeglich, schluessel } = await api<{ moeglich: boolean; schluessel: string | null }>("/push/vapid-key");
  if (!moeglich || !schluessel) return { fehler: "server_aus" };

  const erlaubnis = await Notification.requestPermission();
  if (erlaubnis !== "granted") return { fehler: erlaubnis === "denied" ? "abgelehnt" : "nicht_erteilt" };

  const registrierung = await wartendeRegistrierung();
  if (!registrierung) return { fehler: "kein_sw" };

  // Ein bestehendes Abo weiterverwenden - erneutes subscribe() mit demselben
  // Schluessel liefert ohnehin dasselbe, ein anderes wuerde scheitern.
  const abo =
    (await registrierung.pushManager.getSubscription()) ??
    (await registrierung.pushManager.subscribe({
      userVisibleOnly: true,
      // BufferSource verlangt in neueren TS-Bibliotheken einen echten
      // ArrayBuffer, kein ArrayBufferLike - Uint8Array liefert zur Laufzeit
      // trotzdem genau das, was die Push-API erwartet.
      applicationServerKey: base64ZuBytes(schluessel) as BufferSource,
    }));

  await api("/push/subscribe", { method: "POST", body: { abo: abo.toJSON() } });
  return { ok: true };
}

/** Abo dieses Geraets beenden (die Browser-Erlaubnis selbst bleibt bestehen). */
export async function pushAusschalten(): Promise<void> {
  if (!pushMoeglichImBrowser()) return;
  const registrierung = await wartendeRegistrierung();
  const abo = await registrierung?.pushManager.getSubscription();
  if (!abo) return;
  await api("/push/subscribe", { method: "DELETE", body: { endpoint: abo.endpoint } }).catch(() => {});
  await abo.unsubscribe().catch(() => {});
}

/**
 * Hat dieses Geraet gerade ein Abo? Ueber getRegistration() statt .ready:
 * loest sich auch dann sofort auf, wenn kein Worker laeuft.
 */
export async function geraetHatAbo(): Promise<boolean> {
  if (!pushMoeglichImBrowser() || Notification.permission !== "granted") return false;
  const registrierung = await navigator.serviceWorker.getRegistration();
  if (!registrierung) return false;
  return Boolean(await registrierung.pushManager.getSubscription());
}
